//Solar Report - v2
//Site Details / Array Details / Inverters
//See slicerReports/solarV2Slicer.js for the visual and table/column targets.

import * as powerbi from 'powerbi-client';
import slicer, { STATE_KEYS } from '../slicerReports/solarV2Slicer';
import {
  getInverterForArray,
  getSiteSelections,
  parseSiteDefaults,
} from '../slicerReports/solarV2Lookup';

let report = null;
let siteDefaults = [];

// Single source of truth for what the slicers must show.
let state = createEmptyState();

// Number of queued/running operations. While > 0 our own setSlicerState calls
// are in flight, so `rendered` must not read slicers back (it would mistake
// a half-applied state for a user change).
let pending = 0;
let queue = Promise.resolve();
let initialRenderApplied = false;

function createEmptyState() {
  return {
    [STATE_KEYS.SITE]: null,
    [STATE_KEYS.ARRAY_DETAILS_ARRAY]: null,
    [STATE_KEYS.INVERTERS_ARRAY]: null,
    [STATE_KEYS.INVERTER]: null,
  };
}

// Runs operations one at a time and never lets an error escape.
const enqueue = (label, operation) => {
  pending++;
  queue = queue
    .then(operation)
    .catch((error) => console.error(`Solar v2: ${label} failed`, error))
    .finally(() => pending--);

  return queue;
};

const getActivePage = async () => {
  const pages = await report.getPages();
  return pages.find((page) => page.isActive) || null;
};

// Step 2: apply the site defaults. Sites without an entry (no arrays) clear the
// array and inverter state, so the previous site's values are not carried over.
const applySiteDefaults = (site) => {
  const selections = getSiteSelections(siteDefaults, site);

  state[STATE_KEYS.ARRAY_DETAILS_ARRAY] = selections?.arrayDetailsArray ?? null;
  state[STATE_KEYS.INVERTERS_ARRAY] = selections?.invertersArray ?? null;
  state[STATE_KEYS.INVERTER] = selections?.inverter ?? null;
};

// Records a user selection in the state. Returns true if it changed anything.
const registerUserChange = (stateKey, value) => {
  if (!value || state[stateKey] === value) return false;

  state[stateKey] = value;

  if (stateKey === STATE_KEYS.SITE) {
    applySiteDefaults(value);
  } else if (stateKey === STATE_KEYS.INVERTERS_ARRAY) {
    const inverter = getInverterForArray(
      siteDefaults,
      state[STATE_KEYS.SITE],
      value
    );

    if (inverter) state[STATE_KEYS.INVERTER] = inverter;
  }

  return true;
};

const applyStateToActivePage = async () => {
  const page = await getActivePage();

  if (!page) return;

  const visuals = await page.getVisuals();
  await slicer.applyStateToPage(page.name, state, visuals);
};

// Compares the slicers of the active page with the stored state; a difference
// is a user change.
const detectUserChanges = async () => {
  const page = await getActivePage();

  if (!page) return;

  const visuals = await page.getVisuals();

  for (const slicerConfig of slicer.getPageSlicers(page.name)) {
    // Nothing stored (e.g. site without arrays): nothing to compare against.
    if (!state[slicerConfig.stateKey]) continue;

    const current = await slicer.getSlicerValue(visuals, slicerConfig);

    if (!current || !registerUserChange(slicerConfig.stateKey, current)) {
      continue;
    }

    await slicer.applyStateToPage(page.name, state, visuals);
    // A site change rewrites every other value, so stop after the first change.
    break;
  }
};

const handleDataSelected = async (data) => {
  if (data?.visual?.type !== 'slicer') return;

  const value = data.dataPoints?.[0]?.identity?.[0]?.equals;
  const page = await getActivePage();

  if (!page || !value) return;

  const slicerConfig = slicer
    .getPageSlicers(page.name)
    .find((item) => item.visual === data.visual.name);

  if (!slicerConfig || !registerUserChange(slicerConfig.stateKey, value)) {
    return;
  }

  await applyStateToActivePage();
};

// Embed a Power BI report in the given HTML element with the given configurations
// Read more about how to embed a Power BI report in your application here: https://go.microsoft.com/fwlink/?linkid=2153590
const getEmbed = async (container, configuration) => {
  const { SiteName, EndUses } = configuration.reportParameters;

  siteDefaults = parseSiteDefaults(EndUses);
  state = createEmptyState();
  state[STATE_KEYS.SITE] = SiteName || null;
  applySiteDefaults(state[STATE_KEYS.SITE]);
  initialRenderApplied = false;
  pending = 0;
  queue = Promise.resolve();

  /*-----------------------------------------------------------------------------------+
  |    Don't change these values here: access token, embed URL and report ID.          |
  |    To make changes to these values:                                                |
  |    1. Save any other code changes to a text editor, as these will be lost.         |
  |    2. Select 'Start over' from the ribbon.                                         |
  |    3. Select a report or use an embed token.                                       |
  +-----------------------------------------------------------------------------------*/
  // Embed the report and display it within the div container.
  const pbi = new powerbi.service.Service(
    powerbi.factories.hpmFactory,
    powerbi.factories.wpmpFactory,
    powerbi.factories.routerFactory
  );

  report = pbi.embed(container, configuration);

  report.off('loaded');
  report.off('rendered');
  report.off('dataSelected');
  report.off('pageChanged');

  report.on('loaded', () => enqueue('loaded', applyStateToActivePage));

  report.on('rendered', () => {
    // The first render after load applies the state again, as the visuals may
    // not have been ready on `loaded`.
    if (!initialRenderApplied) {
      initialRenderApplied = true;
      enqueue('first render', applyStateToActivePage);
      return;
    }

    if (pending > 0) return;

    enqueue('rendered', detectUserChanges);
  });

  report.on('dataSelected', (event) =>
    enqueue('dataSelected', () => handleDataSelected(event.detail))
  );

  // Sync groups may not carry API-set state to pages that were not active, so
  // re-apply the stored state to the page being shown.
  report.on('pageChanged', () => enqueue('pageChanged', applyStateToActivePage));

  return report;
};

// Refresh the report
export const refreshSolarV2Data = async () => {
  try {
    await report.refresh();
    return Promise.resolve();
  } catch (errors) {
    return Promise.reject(errors);
  }
};

export default {
  getEmbed,
};
